import { ref, type Ref } from "vue";
import {
  formatErrorMessage,
  isProjectAssetReference,
  resolveManagedAssetPath,
} from "../lib/projectAssets";
import {
  cropProjectAsset,
  readAssetDataUrl,
  rotateProjectAsset,
} from "../lib/imageAssets";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";

import type { EditorImage } from "../editor/port";

/** Document edits the toolbar makes; the editor supplies them. */
export type ImageDocOps = {
  remove(image: EditorImage): void;
  /** The image as it now reads in the document; Nothing if it moved. */
  updateAlt(image: EditorImage, alt: string): Maybe<EditorImage>;
};

/**
 * Image toolbar, crop dialog, and alt-text dialog: everything that opens
 * when a user clicks an image in the prose. `refreshImages` (owned by the
 * editor shell) re-resolves image URLs after an edit rewrites an asset.
 */
export function useImageToolbar(options: {
  /** Just while an editor is mounted. */
  imageOps: () => Maybe<ImageDocOps>;
  shell: Ref<Maybe<HTMLElement>>;
  assetRoot: Ref<Maybe<string>>;
  refreshImages: () => void;
  /** Hide the (mobile) format bubble when the toolbar takes over. */
  hideFormatBubble: () => void;
}): {
  activeImage: Ref<Maybe<EditorImage>>;
  activeAssetPath: Ref<
    | { readonly tag: "nothing" }
    | { readonly tag: "just"; readonly value: string },
    | { readonly tag: "nothing" }
    | Maybe<string>
    | { readonly tag: "just"; readonly value: string }
  >;
  activePreviewSrc: Ref<
    | { readonly tag: "nothing" }
    | { readonly tag: "just"; readonly value: string },
    | { readonly tag: "nothing" }
    | Maybe<string>
    | { readonly tag: "just"; readonly value: string }
  >;
  imageToolbarVisible: Ref<boolean, boolean>;
  imageToolbarAnchor: Ref<
    | { readonly tag: "nothing" }
    | { readonly tag: "just"; readonly value: HTMLElement },
    | { readonly tag: "nothing" }
    | Maybe<HTMLElement>
    | { readonly tag: "just"; readonly value: HTMLElement }
  >;
  imageToolbarBusy: Ref<boolean, boolean>;
  cropDialogOpen: Ref<boolean, boolean>;
  cropDialogDataUrl: Ref<string, string>;
  altDialogOpen: Ref<boolean, boolean>;
  closeImageToolbar: (clearSelection?: boolean) => void;
  openImageToolbar: (image: EditorImage, element: HTMLElement) => void;
  onImageToolbarDelete: () => Promise<void>;
  onImageToolbarRotate: () => Promise<void>;
  onImageToolbarCrop: () => Promise<void>;
  onImageToolbarDescribe: () => void;
  onAltDialogApply: (alt: string) => void;
  onAltDialogClose: () => void;
  onImageCropApply: (rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => Promise<void>;
  onCropDialogClose: () => void;
} {
  const activeImage = ref<Maybe<EditorImage>>(nothing);
  const activeAssetPath = ref<Maybe<string>>(nothing);
  const activePreviewSrc = ref<Maybe<string>>(nothing);
  const imageToolbarVisible = ref(false);
  /** The image widget itself, so the toolbar tracks it as the prose reflows. */
  const imageToolbarAnchor = ref<Maybe<HTMLElement>>(nothing);
  const imageToolbarBusy = ref(false);
  const cropDialogOpen = ref(false);
  const cropDialogDataUrl = ref("");
  const altDialogOpen = ref(false);

  function closeImageToolbar(clearSelection = true): void {
    imageToolbarVisible.value = false;
    if (
      clearSelection === true &&
      cropDialogOpen.value === false &&
      altDialogOpen.value === false
    ) {
      activeImage.value = nothing;
      activeAssetPath.value = nothing;
      activePreviewSrc.value = nothing;
    }
  }

  function openImageToolbar(image: EditorImage, element: HTMLElement): void {
    if (isNothing(options.shell.value)) return;
    const img = element.querySelector("img");
    activeImage.value = just(image);
    activePreviewSrc.value = toMaybe(img?.src);
    activeAssetPath.value = resolveManagedAssetPath(
      options.assetRoot.value,
      image.src,
      activePreviewSrc.value,
    );
    // The toolbar positions itself against this element, flipping above it
    // when it's too near the bottom of the window.
    imageToolbarAnchor.value = just(element);
    imageToolbarVisible.value = true;
    options.hideFormatBubble();
  }

  function assetPathForTools(): Maybe<string> {
    return activeAssetPath.value;
  }

  function requireAssetPath(action: string): Maybe<string> {
    const pathM = assetPathForTools();
    const rootM = options.assetRoot.value;
    if (isNothing(rootM)) {
      window.alert(`${action} requires a saved project or file location.`);
      return nothing;
    }
    if (isNothing(pathM) || isProjectAssetReference(pathM.value) === false) {
      window.alert(
        `${action} is only available for images stored in the Assets folder.`,
      );
      return nothing;
    }
    return pathM;
  }

  function docOps(): Maybe<ImageDocOps> {
    return options.imageOps();
  }

  async function onImageToolbarDelete(): Promise<void> {
    const imageM = activeImage.value;
    const opsM = docOps();
    if (isNothing(imageM) || isNothing(opsM)) return;
    closeImageToolbar();
    opsM.value.remove(imageM.value);
    options.refreshImages();
  }

  async function onImageToolbarRotate(): Promise<void> {
    const imageM = activeImage.value;
    const rootM = options.assetRoot.value;
    if (
      isNothing(imageM) ||
      isNothing(rootM) ||
      imageToolbarBusy.value === true
    ) {
      return;
    }
    const pathM = requireAssetPath("Rotate");
    if (isNothing(pathM)) return;
    imageToolbarBusy.value = true;
    try {
      await rotateProjectAsset(
        rootM.value,
        pathM.value,
        activePreviewSrc.value,
      );
      options.refreshImages();
    } catch (error) {
      console.error("Failed to rotate image", error);
      window.alert(formatErrorMessage(error, "Could not rotate image."));
    } finally {
      imageToolbarBusy.value = false;
    }
  }

  async function onImageToolbarCrop(): Promise<void> {
    const rootM = options.assetRoot.value;
    const pathM = requireAssetPath("Crop");
    if (isNothing(rootM) || isNothing(pathM)) return;
    try {
      cropDialogDataUrl.value = await readAssetDataUrl(
        rootM.value,
        pathM.value,
        activePreviewSrc.value,
      );
      imageToolbarVisible.value = false;
      cropDialogOpen.value = true;
    } catch (error) {
      console.error("Failed to open crop dialog", error);
      window.alert(formatErrorMessage(error, "Could not open crop dialog."));
    }
  }

  function onImageToolbarDescribe(): void {
    if (isNothing(activeImage.value)) return;
    imageToolbarVisible.value = false;
    altDialogOpen.value = true;
  }

  function onAltDialogApply(alt: string): void {
    const imageM = activeImage.value;
    const opsM = docOps();
    if (isNothing(imageM) || isNothing(opsM)) {
      altDialogOpen.value = false;
      return;
    }
    const updated = opsM.value.updateAlt(imageM.value, alt);
    altDialogOpen.value = false;
    if (isJust(updated)) {
      activeImage.value = updated;
    } else {
      activeImage.value = nothing;
      activeAssetPath.value = nothing;
      activePreviewSrc.value = nothing;
    }
  }

  function onAltDialogClose(): void {
    altDialogOpen.value = false;
    activeImage.value = nothing;
    activeAssetPath.value = nothing;
    activePreviewSrc.value = nothing;
  }

  async function onImageCropApply(rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): Promise<void> {
    const rootM = options.assetRoot.value;
    const pathM = assetPathForTools();
    if (isNothing(rootM) || isNothing(pathM)) return;
    imageToolbarBusy.value = true;
    try {
      await cropProjectAsset(
        rootM.value,
        pathM.value,
        rect,
        activePreviewSrc.value,
      );
      cropDialogOpen.value = false;
      activeImage.value = nothing;
      activeAssetPath.value = nothing;
      activePreviewSrc.value = nothing;
      options.refreshImages();
    } catch (error) {
      console.error("Failed to crop image", error);
      window.alert(formatErrorMessage(error, "Could not crop image."));
    } finally {
      imageToolbarBusy.value = false;
    }
  }

  function onCropDialogClose(): void {
    cropDialogOpen.value = false;
    activeImage.value = nothing;
    activeAssetPath.value = nothing;
    activePreviewSrc.value = nothing;
  }

  return {
    activeImage,
    activeAssetPath,
    activePreviewSrc,
    imageToolbarVisible,
    imageToolbarAnchor,
    imageToolbarBusy,
    cropDialogOpen,
    cropDialogDataUrl,
    altDialogOpen,
    closeImageToolbar,
    openImageToolbar,
    onImageToolbarDelete,
    onImageToolbarRotate,
    onImageToolbarCrop,
    onImageToolbarDescribe,
    onAltDialogApply,
    onAltDialogClose,
    onImageCropApply,
    onCropDialogClose,
  };
}
